import { resources } from "@constants/hardcoded-strings";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";
import { MESSAGING_TEMPLATE_TYPE_OPTIONS } from "@constants/messaging-template-type-options";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { MessagingTemplatesCell } from "@containers/messaging-templates/messaging-templates-table-cell/messaging-templates-table-cell";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedMessagingTemplateConfiguration } from "@idw-types/data/messaging-template";
import {
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    TableState,
    useReactTable
} from "@tanstack/react-table";import { sortStrings } from "@utils/string-util";
import Badge from "react-bootstrap/Badge";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import styles from "./messaging-templates-table-container.less";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type MessagingTemplatesTableContainerProps = {
    messagingTemplates: Array<FormattedMessagingTemplateConfiguration>;
    tableState: TableState;
    isPageHeight: boolean;
};

type MessagingTemplatesTableDataRow = FormattedMessagingTemplateConfiguration & {
    companyName: string;
    relyingPartyName: string;
};

export const MessagingTemplatesTableContainer = (
    props: MessagingTemplatesTableContainerProps
) => {

    const { messagingTemplates, tableState, isPageHeight } = props;
    const dispatch = useDispatch();

    const messagingTemplateTableData: Array<MessagingTemplatesTableDataRow> = useMemo(() => {
        return messagingTemplates.map(template => ({
            ...template,
            companyName: template.company?.name || "IDDataweb (Public)",
            relyingPartyName: template.relyingParty?.name || ""
        }));
    }, [messagingTemplates]);

    const columnHelper = createColumnHelper<MessagingTemplatesTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.messagingTemplates.columns.name.header,
            filterFn: "fuzzyText",
            cell: (props) => (
                <MessagingTemplatesCell
                    value={props.getValue()}
                    messagingTemplate={props.row.original}
                />
            )
        }),
        columnHelper.accessor("type", {
            header: resources.tables.messagingTemplates.columns.type.header,
            filterFn: "singleSelectFilter",
            cell: (props) => <div>{props.row.original.type}</div>,
            meta: {
                filterOptions: MESSAGING_TEMPLATE_TYPE_OPTIONS
            },
        }),
        columnHelper.accessor("companyName", {
            header: resources.tables.messagingTemplates.columns.organization.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.companyName, b.original.companyName)
        }),
        columnHelper.accessor("relyingPartyName", {
            header: resources.tables.messagingTemplates.columns.relyingParty.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.relyingPartyName, b.original.relyingPartyName)
        }),
        columnHelper.accessor("status", {
            header: resources.tables.messagingTemplates.columns.status.header,
            filterFn: "singleSelectFilter",
            cell: (props) => {
                switch (props.getValue()) {
                    case EntityStatusTypes.ACTIVE:
                        return <Badge bg={"success"}>{resources.common.active}</Badge>;
                    case EntityStatusTypes.INACTIVE:
                        return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }
            },
            meta: {
                filterOptions: ENTITY_STATUS_OPTIONS
            },
        })
    ]), [messagingTemplateTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: messagingTemplateTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.MESSAGING_TEMPLATES, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.MESSAGING_TEMPLATES, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.MESSAGING_TEMPLATES, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.messagingTemplatesTableContainer}>
            <IdwTable
                id="messaging-templates"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"messaging-templates"}
                className={styles.messagingTemplatesTable}
            />
            <PaginationContainer
                idPrefix="messaging-templates"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};